<template>
  <div id="catalog-items">
    <div class="header-container">
      <div class="header">Catalog Items</div>
      <div class="actions">
        <b-tooltip v-if="canImportCatalogItems" label="Import from file" position="is-left">
          <b-button class="is-light" icon-left="upload" @click="importModalActive = true">Import</b-button>
        </b-tooltip>
        <b-tooltip label="Create a new Catalog Item" position="is-left">
          <b-button class="is-primary" icon-right="plus" @click="newCatalogItem"></b-button>
        </b-tooltip>
      </div>
    </div>
    <catalog-item-import-modal
      v-if="canImportCatalogItems"
      :active="importModalActive"
      @update:active="importModalActive = $event"
    />
    <search-list
      index-name="catalogItems"
      :facets="facets"
      :is-reportable="true"
      :numeric-menu-attributes="numericMenuAttributes"
    >
      <template #custom-date="{ props }">
        <div class="date-header">Last Updated</div>
        <ul class="ais-NumericMenu-list">
          <li
            class="ais-NumericMenu-item"
            v-for="(item, index) in props.items"
            :key="item.label"
            :class="{ 'ais-NumericMenu-item--selected': index === 0 }"
          >
            <label class="ais-NumericMenu-label">
              <input
                class="ais-NumericMenu-radio"
                type="radio"
                name="NumericMenu"
                :checked="index === 0"
                @change="refineCustom(props.refine, item)"
              />
              <span class="ais-NumericMenu-labelText">{{ item.label }}</span>
            </label>
            <template v-if="item.label === 'Date Range'">
              <b-datepicker
                v-model="dates"
                :date-formatter="formatDateRange"
                :disabled="customDatesDisabled"
                :show-week-number="false"
                placeholder="Choose range"
                :icon-right="dates ? 'close-circle' : ''"
                icon-right-clickable
                @icon-right-click="clearDates"
                trap-focus
                range
              ></b-datepicker>
            </template>
          </li>
        </ul>
      </template>
      <template #results="{ items }">
        <div v-if="selectedCount > 0" class="bulk-toolbar">
          <span class="bulk-toolbar__count">{{ selectedCount }} selected</span>
          <div class="bulk-toolbar__actions">
            <b-button class="is-small is-success" icon-left="check" :loading="bulkLoading" @click="bulkActivate">
              Activate
            </b-button>
            <b-button class="is-small is-warning" icon-left="ban" :loading="bulkLoading" @click="bulkDeactivate">
              Deactivate
            </b-button>
            <b-tooltip
              :active="!canBulkDelete"
              label="Only deactivated items can be deleted"
              position="is-top"
              multilined
            >
              <b-button
                class="is-small is-danger"
                icon-left="trash"
                :disabled="!canBulkDelete"
                :loading="bulkLoading"
                @click="bulkDelete"
              >
                Delete
              </b-button>
            </b-tooltip>
            <b-button class="is-small" icon-left="times" :disabled="bulkLoading" @click="clearSelection">
              Clear
            </b-button>
          </div>
        </div>
        <div v-if="bulkLoading && bulkProgress.total > 0" class="bulk-progress">
          <b-progress
            :value="bulkProgress.completed"
            :max="bulkProgress.total"
            type="is-primary"
            size="is-small"
            show-value
            format="raw"
          >
            {{ bulkProgress.completed }} / {{ bulkProgress.total }} batches
          </b-progress>
        </div>
        <table class="table is-striped">
          <thead>
            <th>
              <b-checkbox
                :value="areAllOnPageSelected(items)"
                :indeterminate="isPageSelectionPartial(items)"
                @input="toggleSelectAll(items, $event)"
              ></b-checkbox>
            </th>
            <th>Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Supplier</th>
            <th>SKU</th>
            <th>Cost Updated</th>
            <th>Actions</th>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.objectID">
              <td>
                <b-checkbox :value="isSelected(item)" @input="toggleSelect(item)"></b-checkbox>
              </td>
              <td>
                <a class="catalog-items__name-link" @click="goToCatalogItemEditPage(item)">
                  <span v-if="item.isImported" class="catalog-items__name-mark">
                    <span class="catalog-items__name-text">
                      <ais-highlight attribute="name" :hit="item"></ais-highlight>
                    </span>
                    <span class="catalog-items__import-wrap">
                      <b-tooltip label="Imported catalog item" position="is-right">
                        <b-icon
                          icon="file-import"
                          type="is-success"
                          size="is-small"
                          class="catalog-items__import-icon"
                        />
                      </b-tooltip>
                    </span>
                  </span>
                  <ais-highlight v-else attribute="name" :hit="item"></ais-highlight>
                </a>
              </td>
              <td>
                <ais-highlight attribute="type" :hit="item"></ais-highlight>
              </td>
              <td>
                <b-tag :type="isItemActive(item) ? 'is-success' : 'is-light'">
                  {{ isItemActive(item) ? 'Active' : 'Inactive' }}
                </b-tag>
              </td>
              <td>
                <ais-highlight attribute="supplier.name" :hit="item"></ais-highlight>
              </td>
              <td>{{ item.supplier?.sku ?? '' }}</td>
              <td>{{ item.costUpdatedAt_timestamp | formatDate('') }}</td>
              <td>
                <b-button class="button is-small" @click="goToCatalogItemEditPage(item)" icon-right="edit"></b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </search-list>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';

import { CATALOG_ITEM_IMPORT_PERMISSION } from '@/service/catalogItemImportJob.service';
import CatalogItemService from '@/service/catalog-items.service';
import { getDateItems, getDateValues } from '@/service/dateFilter.service';

import CatalogItemImportModal from '@/components/catalog-import/CatalogItemImportModal.vue';
import SearchList from '@/components/list/searchList.vue';

const defaultNumSearch = '%7B%7D';

function isActiveHit(item) {
  return item?.isActive ?? true;
}

export default {
  name: 'CatalogItemsPage',
  components: { SearchList, CatalogItemImportModal },
  data() {
    const rangeStart = Math.ceil(dayjs().startOf('day').toDate().valueOf());
    const rangeEnd = Math.ceil(dayjs().add(1, 'day').endOf('day').valueOf());
    return {
      isLoading: false,
      importModalActive: false,
      bulkLoading: false,
      bulkProgress: { completed: 0, total: 0 },
      selected: {},
      facets: [
        { facet: 'type', displayName: 'Type' },
        { facet: 'supplier.name', displayName: 'Supplier' },
      ],
      dates: [],
      startDateRange: rangeStart,
      endDateRange: rangeEnd,
      customDateRefine: null,
      customDatesDisabled: true,
      numericMenuAttributes: [
        {
          displayName: 'Last Updated',
          attribute: 'updatedAt_timestamp',
          values: getDateValues(),
          items: [
            { label: 'No date filter' },
            ...getDateItems(new Date()),
            {
              label: 'Date Range',
              start: rangeStart,
              end: rangeEnd,
            },
          ],
        },
      ],
    };
  },
  computed: {
    ...mapGetters('auth', ['hasPermission']),
    canImportCatalogItems() {
      return this.hasPermission(CATALOG_ITEM_IMPORT_PERMISSION);
    },
    selectedList() {
      return Object.values(this.selected);
    },
    selectedCount() {
      return this.selectedList.length;
    },
    selectedIds() {
      return Object.keys(this.selected);
    },
    canBulkDelete() {
      return this.selectedCount > 0 && this.selectedList.every(item => !isActiveHit(item));
    },
  },
  watch: {
    dates(newVal) {
      if (newVal.length) {
        const [start, end] = newVal;
        const startString = Math.floor(dayjs(start).startOf('day').toDate().valueOf());
        const endString = Math.ceil(dayjs(end).endOf('day').toDate().valueOf());

        if (this.customDateRefine) {
          //%7B%22start%22:1660390089471,%22end%22:1662982089471%7D - format of algolia numeric filter
          const formatted = `%7B%22start%22:${startString},%22end%22:${endString}%7D`;
          this.customDateRefine(formatted);
        }
      }
    },
  },
  methods: {
    formatDateRange(dates) {
      return dates.map(d => dayjs(d).format('M/D/YY')).join(' - ');
    },
    clearDates() {
      this.dates = [];
    },
    refineCustom(refine, item) {
      this.customDateRefine = refine;
      const isDateRange = item.label === 'Date Range';
      if (!isDateRange) {
        this.customDatesDisabled = true;
        this.clearDates();
        return refine(item.value);
      }

      this.customDatesDisabled = !isDateRange;
      refine(defaultNumSearch);
    },
    newCatalogItem() {
      this.$router.push({
        name: 'catalog-item-edit',
        params: { catalogItemId: 'new' },
      });
    },
    goToCatalogItemEditPage(catalogItem) {
      this.$router.push({
        name: 'catalog-item-edit',
        params: { catalogItemId: catalogItem.objectID },
      });
    },
    isItemActive(item) {
      return isActiveHit(item);
    },
    isSelected(item) {
      return Boolean(this.selected[item.objectID]);
    },
    toggleSelect(item) {
      const next = { ...this.selected };
      if (next[item.objectID]) delete next[item.objectID];
      else next[item.objectID] = item;
      this.selected = next;
    },
    areAllOnPageSelected(items) {
      return items.length > 0 && items.every(item => this.isSelected(item));
    },
    isPageSelectionPartial(items) {
      const selectedOnPage = items.filter(item => this.isSelected(item)).length;
      return selectedOnPage > 0 && selectedOnPage < items.length;
    },
    toggleSelectAll(items, checked) {
      const next = { ...this.selected };
      items.forEach(item => {
        if (checked) next[item.objectID] = item;
        else delete next[item.objectID];
      });
      this.selected = next;
    },
    clearSelection() {
      this.selected = {};
    },
    async runBulk(action, successMessage) {
      this.bulkLoading = true;
      this.bulkProgress = { completed: 0, total: 0 };
      const onProgress = progress => {
        this.bulkProgress = progress;
      };
      try {
        await action(onProgress);
        this.$buefy.toast.open({ message: successMessage, type: 'is-success' });
        this.clearSelection();
      } catch (e) {
        this.$buefy.notification.open({ message: e.message, type: 'is-danger' });
      } finally {
        this.bulkLoading = false;
        this.bulkProgress = { completed: 0, total: 0 };
      }
    },
    bulkActivate() {
      return this.runBulk(
        onProgress => CatalogItemService.bulkSetActive(this.selectedIds, true, onProgress),
        `Activated ${this.selectedCount} item(s)`
      );
    },
    bulkDeactivate() {
      return this.runBulk(
        onProgress => CatalogItemService.bulkSetActive(this.selectedIds, false, onProgress),
        `Deactivated ${this.selectedCount} item(s)`
      );
    },
    bulkDelete() {
      if (!this.canBulkDelete) return;
      const count = this.selectedCount;
      this.$buefy.dialog.confirm({
        title: 'Delete Catalog Items',
        message: `Are you sure you want to delete <b>${count}</b> item(s)? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () =>
          this.runBulk(
            onProgress => CatalogItemService.bulkSoftDelete(this.selectedIds, onProgress),
            `Deleted ${count} item(s)`
          ),
      });
    },
  },
};
</script>

<style scoped lang="scss">
#catalog-items {
  .header-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    .actions {
      display: flex;
      flex-direction: row;
      *:not(:last-child) {
        margin-right: 8px;
      }
    }
  }
  .is-well {
    background-color: #efefef;
    border: #ddd 1px solid;
    border-radius: 5px;
  }
  .bulk-toolbar {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    background-color: #f2f3ff;
    border: 1px solid #cacfff;
    border-radius: 5px;
    padding: 8px 12px;
    margin-bottom: 10px;
    .bulk-toolbar__count {
      font-weight: 600;
      color: #2b3cbb;
    }
    .bulk-toolbar__actions {
      display: flex;
      flex-direction: row;
      *:not(:last-child) {
        margin-right: 8px;
      }
    }
    .bulk-progress {
      flex-basis: 100%;
      margin-top: 8px;
    }
  }
  .catalog-items__name-link {
    display: inline-block;
  }
  .catalog-items__name-mark {
    position: relative;
    display: inline-block;
  }
  .catalog-items__import-wrap {
    position: absolute;
    left: 100%;
    margin-left: 0.04em;
    top: -0.12em;
    line-height: 0;
    font-size: 0.58em;
  }
  .catalog-items__import-wrap :deep(.icon) {
    width: 1em;
    height: 1em;
  }
  .catalog-items__import-wrap :deep(i.mdi) {
    font-size: 1em !important;
    line-height: 1;
  }
  .catalog-items__import-icon {
    cursor: default;
  }
}
</style>
